import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Loader2, PackageOpen, Search, X, SearchX, FileDown, FileSpreadsheet, FileText, AlertTriangle, Wallet, Upload, Flag, Camera, ArchiveRestore, Layers, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { TripExpenses } from "../components/TripExpenses";
import { BulkImportDialog } from "../components/BulkImportDialog";
import { FotoBuktiDialog } from "../components/FotoBuktiDialog";
import { PaketBiayaDialog } from "../components/PaketBiayaDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../api";

const statusClass = {
  Draft: "bg-slate-100 text-slate-700 border border-slate-200",
  Berangkat: "bg-blue-100 text-blue-800 border border-blue-200",
  Bongkar: "bg-amber-100 text-amber-800 border border-amber-200",
  Selesai: "bg-emerald-100 text-emerald-800 border border-emerald-200",
};

const fmtNum = (v) => (v == null ? "-" : Number(v).toLocaleString("id-ID"));
const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v + "T00:00:00");
  return isNaN(d) ? v : d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};

export default function TripsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user && ["owner", "admin_operasional"].includes(user.role);
  const canExpense = user && ["owner", "kasir"].includes(user.role);
  const canFoto = user && user.role === "owner";
  const [trips, setTrips] = useState(null);
  const [expenseTrip, setExpenseTrip] = useState(null);
  const [fotoTrip, setFotoTrip] = useState(null);
  const [staleIds, setStaleIds] = useState(new Set());
  const [tolerances, setTolerances] = useState({ CPO: 10, "Palm Kernel": 10, Cangkang: 30 });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [paketOpen, setPaketOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkHasil, setBulkHasil] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("semua");
  const [komoditasFilter, setKomoditasFilter] = useState("semua");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const filtered = useMemo(() => {
    if (!trips) return [];
    const q = search.trim().toLowerCase();
    return trips.filter((t) => {
      if (statusFilter !== "semua" && t.Status_Trip !== statusFilter) return false;
      if (komoditasFilter !== "semua" && t.Jenis_Komoditas !== komoditasFilter) return false;
      if (dateFrom && (!t.Tanggal_Muat || t.Tanggal_Muat < dateFrom)) return false;
      if (dateTo && (!t.Tanggal_Muat || t.Tanggal_Muat > dateTo)) return false;
      if (!q) return true;
      return [t.ID_Trip, t.No_DO, t.Plat_Truk, t.Nama_Supir, t.PKS_Asal, t.Tujuan_Bongkar]
        .some((v) => v && String(v).toLowerCase().includes(q));
    });
  }, [trips, search, statusFilter, komoditasFilter, dateFrom, dateTo]);

  const hasFilter = search.trim() !== "" || statusFilter !== "semua" || komoditasFilter !== "semua" || dateFrom !== "" || dateTo !== "";
  const resetFilters = () => { setSearch(""); setStatusFilter("semua"); setKomoditasFilter("semua"); setDateFrom(""); setDateTo(""); };

  const exportTrips = async (fmt) => {
    try {
      const params = { fmt, arsip: showArsip ? 1 : 0 };
      if (search.trim()) params.q = search.trim();
      if (statusFilter !== "semua") params.status = statusFilter;
      if (komoditasFilter !== "semua") params.komoditas = komoditasFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get("/trips/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${showArsip ? "Arsip_Trip" : "Daftar_Trip"}.${fmt === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${showArsip ? "Arsip trip" : "Daftar trip"} diekspor ke ${fmt.toUpperCase()}`);
    } catch (err) {
      toast.error("Gagal mengekspor data");
    }
  };

  const downloadPdf = async (t) => {
    try {
      const res = await api.get(`/trips/${t.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Surat_Jalan_${t.ID_Trip}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Surat Jalan ${t.ID_Trip} diunduh`);
    } catch (err) {
      toast.error("Gagal mengunduh PDF");
    }
  };

  const [showArsip, setShowArsip] = useState(false);
  const [arsipDialog, setArsipDialog] = useState(null);
  const [arsipRunning, setArsipRunning] = useState(false);

  const load = () => {
    api.get(`/trips${showArsip ? "?arsip=1" : ""}`).then(({ data }) => setTrips(data)).catch(() => setTrips([]));
    api.get("/trips/alerts").then(({ data }) => setStaleIds(new Set(data.trips.map((t) => t.id)))).catch(() => {});
    api.get("/tolerances").then(({ data }) => setTolerances(data)).catch(() => {});
  };
  useEffect(() => { setTrips(null); load(); }, [showArsip]); // eslint-disable-line react-hooks/exhaustive-deps

  const openArsipDialog = async () => {
    try {
      const { data } = await api.get("/trips/arsip-kandidat");
      setArsipDialog(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const runArsip = async () => {
    setArsipRunning(true);
    try {
      const { data } = await api.post("/trips/arsipkan");
      toast.success(data.message, { duration: 6000 });
      setArsipDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setArsipRunning(false);
    }
  };

  const restoreArsip = async (t) => {
    try {
      await api.put(`/trips/${t.id}/arsip?arsip=0`);
      toast.success(`Trip ${t.ID_Trip} dikembalikan dari arsip`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/trips/${deleteTarget.id}`);
      toast.success(`Trip ${deleteTarget.ID_Trip} dihapus`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectableIds = (filtered || []).filter((t) => t.Status_Trip !== "Draft").map((t) => t.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));

  const runBulkPaket = async () => {
    setBulkRunning(true);
    try {
      const { data } = await api.post("/trips/expenses/paket-massal", { trip_ids: [...selected] });
      setBulkHasil(data);
      setBulkConfirm(false);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBulkRunning(false);
    }
  };

  return (
    <div data-testid="trips-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">Manajemen Perjalanan Truk</h1>
          <p className="text-sm text-zinc-500 mt-1">{showArsip ? "Arsip trip lama — data tetap dihitung di laporan" : "Data Trip Surat Jalan armada pengangkutan"}</p>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
          <div className="flex rounded-md border border-zinc-300 overflow-hidden col-span-2 sm:col-span-1" data-testid="arsip-toggle">
            <button data-testid="tab-trip-aktif" onClick={() => setShowArsip(false)}
              className={`flex-1 px-3 py-2 text-sm font-medium transition-colors duration-200 ${!showArsip ? "bg-emerald-700 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
              Aktif
            </button>
            <button data-testid="tab-trip-arsip" onClick={() => setShowArsip(true)}
              className={`flex-1 px-3 py-2 text-sm font-medium transition-colors duration-200 ${showArsip ? "bg-emerald-700 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
              Arsip
            </button>
          </div>
          {canFoto && !showArsip && (
            <Button data-testid="arsipkan-button" variant="outline" onClick={openArsipDialog}
              className="border-amber-300 text-amber-800 hover:bg-amber-50 transition-colors duration-200 flex-1 sm:flex-none btn-touch">
              <PackageOpen className="w-4 h-4 mr-2" /> Arsipkan Tahun Lalu
            </Button>
          )}
          {canFoto && !showArsip && (
            <Button data-testid="paket-biaya-button" variant="outline" onClick={() => setPaketOpen(true)}
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 transition-colors duration-200 flex-1 sm:flex-none btn-touch">
              <Layers className="w-4 h-4 mr-2" /> Paket Biaya
            </Button>
          )}
          {canExpense && !showArsip && selected.size > 0 && (
            <Button data-testid="terapkan-paket-massal-button" onClick={() => setBulkConfirm(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white transition-colors duration-200 flex-1 sm:flex-none btn-touch">
              <CheckSquare className="w-4 h-4 mr-2" /> Terapkan Paket ({selected.size})
            </Button>
          )}
          <Button data-testid="export-excel-button" variant="outline" onClick={() => exportTrips("xlsx")}
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 transition-colors duration-200 flex-1 sm:flex-none">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button data-testid="export-csv-button" variant="outline" onClick={() => exportTrips("csv")}
            className="border-zinc-300 text-zinc-700 hover:bg-zinc-50 transition-colors duration-200 flex-1 sm:flex-none">
            <FileText className="w-4 h-4 mr-2" /> CSV
          </Button>
          {canWrite && (
            <Button data-testid="bulk-import-button-trips" variant="outline" onClick={() => setImportOpen(true)}
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 transition-colors duration-200 flex-1 sm:flex-none btn-touch">
              <Upload className="w-4 h-4 mr-2" /> Impor
            </Button>
          )}
          {canWrite && (
            <Button data-testid="add-trip-button" onClick={() => navigate("/trip/baru")}
              className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200 flex-1 sm:flex-none btn-touch">
              <Plus className="w-4 h-4 mr-2" /> Tambah Trip
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg shadow-sm p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="trip-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari ID Trip, No DO, plat truk, supir, PKS, tujuan..." className="pl-9 bg-white" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="filter-status-trigger" className="bg-white sm:w-44"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="semua">Semua Status</SelectItem>
            {Object.keys(statusClass).map((s) => <SelectItem key={s} value={s} data-testid={`filter-status-${s}`}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={komoditasFilter} onValueChange={setKomoditasFilter}>
          <SelectTrigger data-testid="filter-komoditas-trigger" className="bg-white sm:w-44"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="semua">Semua Komoditas</SelectItem>
            {["CPO", "Palm Kernel", "Cangkang"].map((k) => <SelectItem key={k} value={k} data-testid={`filter-komoditas-${k}`}>{k}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 min-w-0">
          <Input data-testid="filter-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-white min-w-0 flex-1 sm:flex-none sm:w-40" title="Tanggal muat dari" />
          <span className="text-xs text-zinc-400 shrink-0">s/d</span>
          <Input data-testid="filter-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-white min-w-0 flex-1 sm:flex-none sm:w-40" title="Tanggal muat sampai" />
        </div>
        {hasFilter && (
          <Button data-testid="reset-filters-button" type="button" variant="outline" onClick={resetFilters} className="border-zinc-300 shrink-0">
            <X className="w-4 h-4 mr-1.5" /> Reset
          </Button>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg shadow-sm overflow-hidden">
        {trips === null ? (
          <div className="flex items-center justify-center py-20 text-zinc-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : trips.length === 0 ? (
          <div data-testid="trips-empty-state" className="flex flex-col items-center justify-center py-20 text-zinc-400">
            <PackageOpen className="w-10 h-10 mb-3" />
            <p className="text-sm">Belum ada data trip. Klik "Tambah Trip" untuk mulai.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="trips-no-results" className="flex flex-col items-center justify-center py-20 text-zinc-400">
            <SearchX className="w-10 h-10 mb-3" />
            <p className="text-sm">Tidak ada trip yang cocok dengan pencarian/filter.</p>
          </div>
        ) : (
          <>
          <div className="md:hidden divide-y divide-zinc-100" data-testid="trips-card-list">
            {filtered.map((t) => {
              const limit = tolerances[t.Jenis_Komoditas];
              const overLimit = t.Penyusutan_KG != null && limit != null && -t.Penyusutan_KG > limit;
              return (
                <div key={t.id} data-testid={`trip-card-${t.ID_Trip}`} className={`p-4 space-y-3 ${overLimit ? "bg-red-50" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-zinc-900 inline-flex items-center gap-1.5">
                      {canExpense && !showArsip && (
                        <input type="checkbox" data-testid={`select-card-${t.ID_Trip}`} checked={selected.has(t.id)}
                          disabled={t.Status_Trip === "Draft"} onChange={() => toggleSelect(t.id)}
                          className="h-4 w-4 accent-emerald-700 mr-1 disabled:opacity-40" />
                      )}
                      {t.ID_Trip}
                      {(t.Tonase_Edit_Count || 0) > 1 && (
                        <Flag data-testid={`flag-card-${t.ID_Trip}`} className="w-4 h-4 text-red-600 fill-red-600" title={`Tonase diedit ${t.Tonase_Edit_Count} kali`} />
                      )}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Badge className={`${statusClass[t.Status_Trip] || ""} font-medium`}>{t.Status_Trip}</Badge>
                      {staleIds.has(t.id) && <AlertTriangle className="w-4 h-4 text-amber-500" title="Berangkat terlalu lama" />}
                    </div>
                  </div>
                  <div className="text-sm text-zinc-600 space-y-0.5">
                    <p>{fmtDate(t.Tanggal_Muat)} • {t.No_DO}</p>
                    <p className="font-medium text-zinc-900">{t.Plat_Truk} — {t.Nama_Supir}</p>
                    <p>{t.Jenis_Komoditas} • {t.PKS_Asal} → {t.Tujuan_Bongkar}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 bg-zinc-50 border border-zinc-200 rounded-md p-2.5 text-center">
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-zinc-500">Muat (KG)</p>
                      <p className="num-hc text-zinc-900">{fmtNum(t.Tonase_Muat_KG)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-zinc-500">Bongkar (KG)</p>
                      <p className="num-hc text-zinc-900">{fmtNum(t.Tonase_Bongkar_KG)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-zinc-500">Susut (KG)</p>
                      <p className={`num-hc inline-flex items-center gap-1 justify-center ${t.Penyusutan_KG > 0 ? "text-emerald-700" : "text-red-800"}`}>
                        {fmtNum(t.Penyusutan_KG)}
                        {overLimit && <AlertTriangle className="w-3.5 h-3.5 text-red-600" />}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    {canExpense ? (
                      <span className="text-sm font-semibold text-emerald-800 tabular-nums">
                        Uang Jalan: Rp {Number(t.Total_Uang_Jalan || 0).toLocaleString("id-ID")}
                      </span>
                    ) : <span />}
                    <div className="flex gap-1">
                      <button data-testid={`card-pdf-trip-${t.ID_Trip}`} onClick={() => downloadPdf(t)}
                        className="p-2 rounded-md text-zinc-500 hover:text-blue-700 hover:bg-blue-50 transition-colors duration-200 icon-touch" title="Unduh Surat Jalan (PDF)">
                        <FileDown className="w-5 h-5" />
                      </button>
                      {canExpense && (
                        <button data-testid={`card-expense-trip-${t.ID_Trip}`} onClick={() => setExpenseTrip(t)}
                          className="p-2 rounded-md text-zinc-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors duration-200 icon-touch" title="Pengeluaran Harian">
                          <Wallet className="w-5 h-5" />
                        </button>
                      )}
                      {canFoto && (
                        <button data-testid={`card-foto-trip-${t.ID_Trip}`} onClick={() => setFotoTrip(t)}
                          className="p-2 rounded-md text-zinc-500 hover:text-sky-700 hover:bg-sky-50 transition-colors duration-200 icon-touch" title="Foto Bukti Timbang">
                          <Camera className="w-5 h-5" />
                        </button>
                      )}
                      {canFoto && showArsip && (
                        <button data-testid={`card-restore-trip-${t.ID_Trip}`} onClick={() => restoreArsip(t)}
                          className="p-2 rounded-md text-zinc-500 hover:text-amber-700 hover:bg-amber-50 transition-colors duration-200 icon-touch" title="Kembalikan dari Arsip">
                          <ArchiveRestore className="w-5 h-5" />
                        </button>
                      )}
                      {canWrite && (
                        <>
                          <button data-testid={`card-edit-trip-${t.ID_Trip}`} onClick={() => navigate(`/trip/${t.id}/edit`)}
                            className="p-2 rounded-md text-zinc-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors duration-200 icon-touch" title="Edit">
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button data-testid={`card-delete-trip-${t.ID_Trip}`} onClick={() => setDeleteTarget(t)}
                            className="p-2 rounded-md text-zinc-500 hover:text-red-600 hover:bg-red-50 transition-colors duration-200 icon-touch" title="Hapus">
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  {canExpense && !showArsip && (
                    <th className="px-3 py-3">
                      <input type="checkbox" data-testid="select-all-trips" checked={allSelected} onChange={toggleAll}
                        className="h-4 w-4 accent-emerald-700 cursor-pointer" title="Pilih semua trip (non-Draft)" />
                    </th>
                  )}
                  {["ID Trip", "Tgl Muat", "No DO", "Plat Truk", "Supir", "Komoditas", "Rute", "Muat (KG)", "Bongkar (KG)", "Susut (KG)", ...(canExpense ? ["Uang Jalan (Rp)"] : []), "Status", "Aksi"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => {
                  const limit = tolerances[t.Jenis_Komoditas];
                  const overLimit = t.Penyusutan_KG != null && limit != null && -t.Penyusutan_KG > limit;
                  return (
                  <tr key={t.id} data-testid={`trip-row-${t.ID_Trip}`}
                    title={overLimit ? `Susut Melebihi Toleransi (> ${limit} KG untuk ${t.Jenis_Komoditas})` : undefined}
                    className={`border-b border-zinc-100 transition-colors duration-200 ${selected.has(t.id) ? "bg-emerald-50/60" : overLimit ? "bg-red-50 hover:bg-red-100" : "hover:bg-zinc-50"}`}>
                    {canExpense && !showArsip && (
                      <td className="px-3 py-3">
                        <input type="checkbox" data-testid={`select-trip-${t.ID_Trip}`} checked={selected.has(t.id)}
                          disabled={t.Status_Trip === "Draft"} onChange={() => toggleSelect(t.id)}
                          className="h-4 w-4 accent-emerald-700 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                          title={t.Status_Trip === "Draft" ? "Trip Draft tidak bisa diberi biaya" : "Pilih trip"} />
                      </td>
                    )}
                    <td className="px-4 py-3 font-medium text-zinc-900 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        {t.ID_Trip}
                        {(t.Tonase_Edit_Count || 0) > 1 && (
                          <Flag data-testid={`flag-trip-${t.ID_Trip}`} className="w-4 h-4 text-red-600 fill-red-600" title={`Tonase diedit ${t.Tonase_Edit_Count} kali`} />
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{fmtDate(t.Tanggal_Muat)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{t.No_DO}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{t.Plat_Truk}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{t.Nama_Supir}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{t.Jenis_Komoditas}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-zinc-600">{t.PKS_Asal} → {t.Tujuan_Bongkar}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-right num-hc text-zinc-900">{fmtNum(t.Tonase_Muat_KG)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-right num-hc text-zinc-900">{fmtNum(t.Tonase_Bongkar_KG)}</td>
                    <td className={`px-4 py-3 whitespace-nowrap text-right num-hc ${t.Penyusutan_KG > 0 ? "text-emerald-700" : "text-red-800"}`}>
                      <span className="inline-flex items-center gap-1.5 justify-end">
                        {fmtNum(t.Penyusutan_KG)}
                        {overLimit && (
                          <AlertTriangle data-testid={`susut-warning-${t.ID_Trip}`} className="w-3.5 h-3.5 text-red-600" title={`Susut Melebihi Toleransi (> ${limit} KG untuk ${t.Jenis_Komoditas})`} />
                        )}
                      </span>
                    </td>
                    {canExpense && (
                      <td data-testid={`uang-jalan-${t.ID_Trip}`} className="px-4 py-3 whitespace-nowrap text-right tabular-nums text-emerald-800 font-medium">
                        {Number(t.Total_Uang_Jalan || 0).toLocaleString("id-ID")}
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <Badge data-testid={`trip-status-${t.ID_Trip}`} className={`${statusClass[t.Status_Trip] || ""} font-medium`}>{t.Status_Trip}</Badge>
                        {staleIds.has(t.id) && (
                          <AlertTriangle data-testid={`stale-icon-${t.ID_Trip}`} className="w-4 h-4 text-amber-500" title="Berangkat terlalu lama" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex gap-1">
                        <button data-testid={`pdf-trip-${t.ID_Trip}`} onClick={() => downloadPdf(t)}
                          className="p-2 rounded-md text-zinc-500 hover:text-blue-700 hover:bg-blue-50 transition-colors duration-200 icon-touch" title="Unduh Surat Jalan (PDF)">
                          <FileDown className="w-4 h-4" />
                        </button>
                        {canExpense && (
                          <button data-testid={`expense-trip-${t.ID_Trip}`} onClick={() => setExpenseTrip(t)}
                            className="p-2 rounded-md text-zinc-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors duration-200 icon-touch" title="Pengeluaran Harian (Uang Jalan)">
                            <Wallet className="w-4 h-4" />
                          </button>
                        )}
                        {canFoto && (
                          <button data-testid={`foto-trip-${t.ID_Trip}`} onClick={() => setFotoTrip(t)}
                            className="p-2 rounded-md text-zinc-500 hover:text-sky-700 hover:bg-sky-50 transition-colors duration-200 icon-touch" title="Foto Bukti Timbang">
                            <Camera className="w-4 h-4" />
                          </button>
                        )}
                        {canFoto && showArsip && (
                          <button data-testid={`restore-trip-${t.ID_Trip}`} onClick={() => restoreArsip(t)}
                            className="p-2 rounded-md text-zinc-500 hover:text-amber-700 hover:bg-amber-50 transition-colors duration-200 icon-touch" title="Kembalikan dari Arsip">
                            <ArchiveRestore className="w-4 h-4" />
                          </button>
                        )}
                        {canWrite && (
                          <>
                            <button data-testid={`edit-trip-${t.ID_Trip}`} onClick={() => navigate(`/trip/${t.id}/edit`)}
                              className="p-2 rounded-md text-zinc-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors duration-200 icon-touch" title="Edit">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button data-testid={`delete-trip-${t.ID_Trip}`} onClick={() => setDeleteTarget(t)}
                              className="p-2 rounded-md text-zinc-500 hover:text-red-600 hover:bg-red-50 transition-colors duration-200 icon-touch" title="Hapus">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      <Dialog open={!!expenseTrip} onOpenChange={(v) => { if (!v) { setExpenseTrip(null); load(); } }}>
        <DialogContent data-testid="expense-dialog" className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pengeluaran Harian — {expenseTrip?.ID_Trip} ({expenseTrip?.Plat_Truk})</DialogTitle>
          </DialogHeader>
          {expenseTrip && <TripExpenses tripId={expenseTrip.id} statusTrip={expenseTrip.Status_Trip} komoditas={expenseTrip.Jenis_Komoditas} />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Trip?</AlertDialogTitle>
            <AlertDialogDescription>
              Trip {deleteTarget?.ID_Trip} akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!arsipDialog} onOpenChange={(open) => !open && setArsipDialog(null)}>
        <AlertDialogContent data-testid="arsip-dialog" className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Arsipkan Trip Tahun Lalu?</AlertDialogTitle>
            <AlertDialogDescription>
              {arsipDialog?.count > 0
                ? `${arsipDialog.count} trip Selesai sebelum ${fmtDate(arsipDialog.cutoff)} akan dipindahkan ke tab Arsip. Data tetap dihitung dalam laporan & laba rugi.`
                : "Tidak ada trip Selesai tahun lalu yang bisa diarsipkan. Sistem juga mengarsipkan trip tahun lalu secara otomatis setiap hari."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-arsip-button">Batal</AlertDialogCancel>
            {arsipDialog?.count > 0 && (
              <AlertDialogAction data-testid="confirm-arsip-button" onClick={runArsip} disabled={arsipRunning}
                className="bg-amber-600 hover:bg-amber-700">
                {arsipRunning ? "Memproses..." : `Arsipkan ${arsipDialog.count} Trip`}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PaketBiayaDialog open={paketOpen} onClose={() => setPaketOpen(false)} />
      <AlertDialog open={bulkConfirm} onOpenChange={(open) => !open && setBulkConfirm(false)}>
        <AlertDialogContent data-testid="bulk-paket-dialog" className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Terapkan Paket Biaya ke {selected.size} Trip?</AlertDialogTitle>
            <AlertDialogDescription>
              Setiap trip akan diisi kategori biaya standar sesuai komoditasnya (CPO / Palm Kernel / Cangkang).
              Tanggal keluar mengikuti Tanggal Muat dan status pencairan langsung <b>Sudah Dicairkan</b>; kategori yang sudah ada di trip akan dilewati. Nominal tetap bisa diubah per trip setelahnya.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-bulk-paket">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-bulk-paket" onClick={(e) => { e.preventDefault(); runBulkPaket(); }} disabled={bulkRunning}
              className="bg-emerald-700 hover:bg-emerald-800">
              {bulkRunning ? "Memproses..." : "Terapkan Sekarang"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={!!bulkHasil} onOpenChange={(v) => !v && setBulkHasil(null)}>
        <DialogContent data-testid="bulk-paket-hasil" className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Hasil Terapkan Paket Biaya</DialogTitle>
          </DialogHeader>
          {bulkHasil && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3">
                  <p className="text-xs text-emerald-700">Trip terisi</p>
                  <p data-testid="bulk-hasil-trip" className="text-lg font-semibold text-emerald-900">{bulkHasil.ringkasan.trip_terisi}/{bulkHasil.ringkasan.trip}</p>
                </div>
                <div className="rounded-md bg-zinc-50 border border-zinc-200 p-3">
                  <p className="text-xs text-zinc-500">Biaya ditambah</p>
                  <p data-testid="bulk-hasil-ditambah" className="text-lg font-semibold text-zinc-900">{bulkHasil.ringkasan.ditambah}</p>
                </div>
                <div className="rounded-md bg-zinc-50 border border-zinc-200 p-3">
                  <p className="text-xs text-zinc-500">Total</p>
                  <p className="text-base font-semibold text-zinc-900 tabular-nums">Rp {Number(bulkHasil.ringkasan.total_rp).toLocaleString("id-ID")}</p>
                </div>
              </div>
              <ul className="max-h-60 overflow-y-auto divide-y divide-zinc-100 border border-zinc-200 rounded-md">
                {bulkHasil.hasil.map((h) => (
                  <li key={h.trip_id} data-testid={`bulk-hasil-${h.ID_Trip}`} className="flex items-center justify-between px-3 py-2">
                    <span className="font-medium text-zinc-800">{h.ID_Trip} <span className="text-xs text-zinc-500">({h.komoditas}{h.jenis_truk ? ` • ${h.jenis_truk}` : ""})</span></span>
                    {h.alasan ? <span className="text-xs text-red-600">{h.alasan}</span>
                      : <span className="text-xs text-zinc-600">+{h.ditambah} kategori{h.dilewati ? `, ${h.dilewati} dilewati` : ""}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <BulkImportDialog entity="trips" open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
      {canFoto && <FotoBuktiDialog trip={fotoTrip} onClose={() => setFotoTrip(null)} />}
    </div>
  );
}

